import { Ban, Check, MoreHorizontal, Plus, Replace, Star, Trash2, UserPlus } from "lucide-react";
import * as React from "react";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader,
  DialogTitle, DiscardGuard,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Callout, ErrorState, TableSkeleton } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { GiveNumberDialog, TakeNumberDialog } from "@/features/campaign-staffing";
import { ApiError } from "@/lib/api";
import { mayBePrimary, numberPlace, numberStanding, numbersByRole, unroutedNumbers } from "@/lib/campaign";
import {
  useAddCampaignNumbers, useCampaignMembers, useCampaignNumbers, useDids, useInboundOnly,
  useRemoveCampaignNumber, useReplaceCampaignNumber, useSetCampaignPrimary,
} from "@/lib/queries";
import type { Campaign, CampaignNumber, QueueMember } from "@/lib/types";
import { formatPhoneNumber } from "@/lib/utils";

/**
 * Every number the campaign uses, in one place (ACL-629, platform ADR-202): its primary (the
 * branded caller id), its local numbers (presented to a lead in their area code or state) and its
 * reps' own numbers (ADR-037).
 *
 * A number flagged as spam, or replaced by another, becomes inbound only: it keeps ringing the
 * campaign, so a lead it called can still call it back, and no call or first text presents it.
 * Removing a number is the one thing that stops it ringing here.
 *
 * Reps' numbers are given and taken with the Staffing tab's own dialogs, which check the rep
 * staffs the campaign's line. Every change here is the same row the Numbers page shows.
 */
export function CampaignNumbers({ campaign, canManage }: { campaign: Campaign; canManage: boolean }) {
  const toast = useToast();
  const { data, isPending, error, refetch } = useCampaignNumbers(campaign.id);
  const { data: members } = useCampaignMembers(campaign.id);
  const inboundOnly = useInboundOnly();
  const setPrimary = useSetCampaignPrimary();
  const remove = useRemoveCampaignNumber();
  const [adding, setAdding] = React.useState(false);
  const [replacing, setReplacing] = React.useState<CampaignNumber | null>(null);
  const [flagging, setFlagging] = React.useState<CampaignNumber | null>(null);
  const [removing, setRemoving] = React.useState<CampaignNumber | null>(null);
  const [giving, setGiving] = React.useState<QueueMember | null>(null);
  const [taking, setTaking] = React.useState<CampaignNumber | null>(null);

  const groups = numbersByRole(data?.numbers ?? []);
  // A rep is given a number on the primary line, which is where the number parks their caller.
  const givable = (members ?? []).filter((m) => m.queue_id === campaign.queue_id && m.sip_endpoint_id !== null);

  /** Runs one write and says how it went; true when it worked, so a confirm closes only then
   *  and a refusal (a 409 such as "is the caller id of …") leaves it open beside the toast. */
  const act = async (work: () => Promise<unknown>, done: string, failed: string): Promise<boolean> => {
    try {
      await work();
      toast.success(done);
      return true;
    } catch (e) {
      toast.error(failed, e instanceof ApiError ? e.message : "Please try again.");
      return false;
    }
  };

  const menu = (n: CampaignNumber) => (
    <NumberMenu
      n={n}
      onPrimary={() =>
        act(
          () => setPrimary.mutateAsync({ campaignId: campaign.id, didId: n.did_id }),
          `${formatPhoneNumber(n.number)} is the primary number`,
          "Could not make it primary",
        )
      }
      onReplace={() => setReplacing(n)}
      onFlag={() => setFlagging(n)}
      onUnflag={() =>
        act(
          () => inboundOnly.mutateAsync({ campaignId: campaign.id, didId: n.did_id, on: false }),
          `${formatPhoneNumber(n.number)} is presented on calls again`,
          "Could not change the number",
        )
      }
      onTake={n.role === "rep" ? () => setTaking(n) : undefined}
      onRemove={() => setRemoving(n)}
    />
  );

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="Numbers"
          description="The numbers this campaign calls from and takes calls on. A flagged or replaced number keeps taking callbacks, and no call presents it."
          action={
            canManage && (
              <Button size="sm" onClick={() => setAdding(true)}>
                <Plus />
                Add numbers
              </Button>
            )
          }
        />
        {isPending ? (
          <CardBody>
            <TableSkeleton rows={3} cols={5} />
          </CardBody>
        ) : error ? (
          <CardBody>
            <ErrorState error={error} action={<Button variant="outline" onClick={() => refetch()}>Try again</Button>} />
          </CardBody>
        ) : (
          <>
            <NumberGroup
              title="Primary"
              description="The branded number: every call presents it unless a step asks for another. It is never inbound only."
              numbers={groups.primary}
              empty={
                data?.caller_id_number
                  ? `${formatPhoneNumber(data.caller_id_number)} is the caller id, but it is not one of the organisation's numbers any more. Make one of the local numbers primary.`
                  : "No primary number. Calls present the rep's own number, then their domain's default. Make one of the local numbers primary."
              }
              menu={canManage ? menu : undefined}
            />
            <NumberGroup
              title="Local numbers"
              description="A Local step presents one in the lead's area code, then their state; a Rotating step takes them in turn."
              numbers={groups.local}
              empty="No local numbers. A Local step presents the primary number instead."
              menu={canManage ? menu : undefined}
            />
            <NumberGroup
              title="Reps' own numbers"
              description="A lead who calls one back reaches the rep who called them first."
              numbers={groups.rep}
              empty="No rep has a number of their own on this campaign."
              menu={canManage ? menu : undefined}
              rep
              action={
                canManage && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" disabled={givable.length === 0}>
                        <UserPlus />
                        Assign to a rep
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel>Reps on the primary line</DropdownMenuLabel>
                      {givable.map((m) => (
                        <DropdownMenuItem key={`${m.user}@${m.domain}`} onSelect={() => setGiving(m)}>
                          {m.display_name ?? m.user}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )
              }
            />
          </>
        )}
      </Card>

      <AddNumbersDialog open={adding} onOpenChange={setAdding} campaign={campaign} />

      <ReplaceNumberDialog
        number={replacing}
        onOpenChange={(open) => !open && setReplacing(null)}
        campaign={campaign}
      />

      <GiveNumberDialog
        member={giving}
        onOpenChange={(open) => !open && setGiving(null)}
        campaign={campaign}
      />

      <TakeNumberDialog
        did={
          taking && {
            id: taking.did_id,
            number: taking.number,
            sip_endpoint_user_name: taking.rep_name,
            sip_endpoint_extension: null,
          }
        }
        onOpenChange={(open) => !open && setTaking(null)}
        campaign={campaign}
      />

      <ConfirmDialog
        open={flagging !== null}
        onOpenChange={(open) => !open && setFlagging(null)}
        title={`Flag ${flagging ? formatPhoneNumber(flagging.number) : "this number"} as spam?`}
        description="It keeps ringing this campaign, so the leads it called can still call it back. No call or first text presents it from now on."
        confirmLabel="Flag it"
        loading={inboundOnly.isPending}
        onConfirm={async () => {
          if (!flagging) return;
          const flagged = await act(
            () => inboundOnly.mutateAsync({ campaignId: campaign.id, didId: flagging.did_id, on: true }),
            `${formatPhoneNumber(flagging.number)} is inbound only`,
            "Could not flag the number",
          );
          if (flagged) setFlagging(null);
        }}
      />

      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => !open && setRemoving(null)}
        title={`Take ${removing ? formatPhoneNumber(removing.number) : "this number"} off the campaign?`}
        description="It stops ringing here: a lead who calls it back hears that it cannot be reached. It stays one of the organisation's numbers, ready to use again."
        confirmLabel="Take it off"
        destructive
        loading={remove.isPending}
        onConfirm={async () => {
          if (!removing) return;
          const removed = await act(
            () => remove.mutateAsync({ campaignId: campaign.id, didId: removing.did_id }),
            `${formatPhoneNumber(removing.number)} is off the campaign`,
            "Could not take the number off",
          );
          if (removed) setRemoving(null);
        }}
      />
    </div>
  );
}

function NumberGroup({
  title, description, numbers, empty, menu, rep = false, action,
}: {
  title: string;
  description: string;
  numbers: CampaignNumber[];
  empty: string;
  menu?: (n: CampaignNumber) => React.ReactNode;
  rep?: boolean;
  action?: React.ReactNode;
}) {
  return (
    <section className="border-b border-border last:border-b-0" aria-label={title}>
      <div className="flex items-start justify-between gap-4 px-4 pb-2 pt-4">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-text">
            {title}
            <span className="ml-1.5 font-normal tabular-nums text-text-muted">{numbers.length}</span>
          </h3>
          <p className="text-xs text-text-muted">{description}</p>
        </div>
        {action}
      </div>
      {numbers.length === 0 ? (
        <p className="px-4 pb-4 text-xs text-text-subtle">{empty}</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              {/* The same widths in every group, so the three tables read as one. */}
              <TableHead className="w-52">Number</TableHead>
              {rep && <TableHead className="w-40">Rep</TableHead>}
              <TableHead className="w-28">Local to</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-28 text-right">Calls today</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {numbers.map((n) => {
              const standing = numberStanding(n);
              return (
                <TableRow key={n.did_id}>
                  <TableCell className="whitespace-nowrap">
                    <span className="font-mono text-xs text-text">{formatPhoneNumber(n.number)}</span>
                    {n.role === "primary" && !n.routed_here && (
                      <span className="ml-2 text-xs text-warning-11" title="A lead who calls it back may not reach this campaign">
                        not routed to this campaign
                      </span>
                    )}
                  </TableCell>
                  {rep && (
                    <TableCell className="whitespace-nowrap">
                      {n.rep_name ?? "—"}
                      {n.rep_on_campaign === false && (
                        <span className="ml-2 text-xs text-warning-11">not on the line any more</span>
                      )}
                    </TableCell>
                  )}
                  <TableCell className="whitespace-nowrap text-text-muted">{numberPlace(n)}</TableCell>
                  <TableCell>
                    <Badge tone={standing.tone} size="sm" title={standing.description}>
                      {standing.label}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{n.calls_today}</TableCell>
                  <TableCell>{menu?.(n)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </section>
  );
}

/** What can be done to one number: the primary is replaced, never flagged or taken off; a rep's
 *  number is taken back as Staffing does. */
function NumberMenu({
  n, onPrimary, onReplace, onFlag, onUnflag, onTake, onRemove,
}: {
  n: CampaignNumber;
  onPrimary: () => void;
  onReplace: () => void;
  onFlag: () => void;
  onUnflag: () => void;
  onTake?: () => void;
  onRemove: () => void;
}) {
  const primary = n.role === "primary";
  const flagged = n.inbound_only_at !== null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${formatPhoneNumber(n.number)}`}>
          <MoreHorizontal />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {n.role === "local" && mayBePrimary(n) && (
          <DropdownMenuItem onSelect={onPrimary}>
            <Star />
            Make primary
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onSelect={onReplace}>
          <Replace />
          Replace…
        </DropdownMenuItem>
        {!primary &&
          (flagged ? (
            <DropdownMenuItem onSelect={onUnflag}>
              <Check />
              Present it again
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem onSelect={onFlag}>
              <Ban />
              Flag as spam
            </DropdownMenuItem>
          ))}
        {onTake && (
          <DropdownMenuItem onSelect={onTake}>
            <UserPlus />
            Take back from the rep…
          </DropdownMenuItem>
        )}
        {!primary && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem destructive onSelect={onRemove}>
              <Trash2 />
              Take off the campaign
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** What both dialogs say when the organisation has no unrouted number to offer. */
const NO_UNROUTED = "Every number the organisation holds is routed somewhere. Ask for more on Numbers.";

/** The organisation's numbers that ring nowhere yet, fetched while a dialog is open: the numbers
 *  added to a campaign or brought in to replace one. `presentableOnly` leaves out a number
 *  flagged before, which as a replacement would be presented by nothing. */
function useUnroutedNumbers(
  campaign: Pick<Campaign, "tenant_id">,
  { open, presentableOnly = false }: { open: boolean; presentableOnly?: boolean },
) {
  const { data: dids, isPending } = useDids({ tenant_id: campaign.tenant_id, action_type: "none" }, { enabled: open });
  const all = unroutedNumbers(dids ?? [], campaign.tenant_id);
  return { choices: presentableOnly ? all.filter((d) => !d.inbound_only_at) : all, isPending };
}

/** The organisation's numbers that ring nowhere yet, as local numbers of this campaign. */
function AddNumbersDialog({
  open, onOpenChange, campaign,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaign: Campaign;
}) {
  const toast = useToast();
  const add = useAddCampaignNumbers();
  const { choices, isPending } = useUnroutedNumbers(campaign, { open });
  const [picked, setPicked] = React.useState<Set<number>>(new Set());
  const [formError, setFormError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setPicked(new Set());
    setFormError(null);
  }, [open]);

  const toggle = (id: number) =>
    setPicked((was) => {
      const next = new Set(was);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);
    try {
      await add.mutateAsync({ campaignId: campaign.id, didIds: [...picked] });
      toast.success(picked.size === 1 ? "Number added" : `${picked.size} numbers added`, campaign.name);
      onOpenChange(false);
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Could not add the numbers.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DiscardGuard changed={picked.size > 0} />
        <DialogHeader>
          <DialogTitle>Add numbers to {campaign.name}</DialogTitle>
          <DialogDescription>
            Each one rings the campaign&apos;s line and becomes one of its local numbers.
          </DialogDescription>
        </DialogHeader>
        <DialogForm onSubmit={onSubmit} noValidate>
          <DialogBody className="space-y-3">
            {formError && <Callout tone="danger">{formError}</Callout>}
            {isPending ? (
              <p className="text-xs text-text-muted">Loading the numbers…</p>
            ) : choices.length === 0 ? (
              <Callout tone="info">{NO_UNROUTED}</Callout>
            ) : (
              <fieldset className="min-w-0">
                <legend className="mb-2 text-xs font-medium text-text">Numbers that don&apos;t ring anywhere yet</legend>
                <div className="grid max-h-72 gap-1.5 overflow-y-auto">
                  {choices.map((d) => (
                    <label
                      key={d.id}
                      className="flex cursor-pointer items-center gap-2.5 rounded-md border border-border px-3 py-2 has-[:checked]:border-accent-8 has-[:checked]:bg-accent-2"
                    >
                      <input
                        type="checkbox"
                        className="accent-accent-9"
                        checked={picked.has(d.id)}
                        onChange={() => toggle(d.id)}
                      />
                      <span className="font-mono text-xs text-text">{formatPhoneNumber(d.number)}</span>
                      {d.inbound_only_at && (
                        <Badge tone="warning" size="sm">
                          Inbound only
                        </Badge>
                      )}
                      {d.description && <span className="truncate text-xs text-text-muted">{d.description}</span>}
                    </label>
                  ))}
                </div>
              </fieldset>
            )}
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="default" loading={add.isPending} disabled={picked.size === 0}>
              {picked.size > 1 ? `Add ${picked.size} numbers` : "Add number"}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}

/** Another of the organisation's unrouted numbers takes this one's place, and this one becomes
 *  inbound only: the usual answer to a number flagged as spam. */
function ReplaceNumberDialog({
  number, onOpenChange, campaign,
}: {
  number: CampaignNumber | null;
  onOpenChange: (open: boolean) => void;
  campaign: Campaign;
}) {
  const toast = useToast();
  const replace = useReplaceCampaignNumber();
  const open = number !== null;
  const { choices, isPending } = useUnroutedNumbers(campaign, { open, presentableOnly: true });
  const [didId, setDidId] = React.useState<number | null>(null);
  const [formError, setFormError] = React.useState<string | null>(null);
  const old = number ? formatPhoneNumber(number.number) : "";
  const takes =
    number?.role === "primary"
      ? "It becomes the primary number, the caller id every call presents."
      : number?.role === "rep"
        ? `It rings ${number.rep_name ?? "the rep"} first, as ${old} did.`
        : "It becomes one of the campaign's local numbers.";

  React.useEffect(() => {
    if (!open) return;
    setDidId(null);
    setFormError(null);
  }, [open]);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!number || didId === null) {
      setFormError("Choose a number.");
      return;
    }
    setFormError(null);
    try {
      await replace.mutateAsync({ campaignId: campaign.id, didId: number.did_id, withDidId: didId });
      toast.success("Number replaced", `${old} is inbound only`);
      onOpenChange(false);
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Could not replace the number.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DiscardGuard changed={didId !== null} />
        <DialogHeader>
          <DialogTitle>Replace {old}</DialogTitle>
          <DialogDescription>{takes}</DialogDescription>
        </DialogHeader>
        <DialogForm onSubmit={onSubmit} noValidate>
          <DialogBody className="space-y-4">
            {formError && <Callout tone="danger">{formError}</Callout>}
            <Field
              label="New number"
              htmlFor="replace-number"
              required
              hint="Numbers the organisation holds that don't ring anywhere yet."
            >
              <Select value={didId === null ? "" : String(didId)} onValueChange={(v) => setDidId(Number(v))}>
                <SelectTrigger id="replace-number">
                  <SelectValue
                    placeholder={
                      isPending ? "Loading the numbers…" : choices.length === 0 ? "No unrouted numbers" : "Choose a number"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {choices.length === 0 && !isPending && (
                    <p className="px-2 py-1.5 text-xs text-text-muted">{NO_UNROUTED}</p>
                  )}
                  {choices.map((d) => (
                    <SelectItem key={d.id} value={String(d.id)} description={d.description ?? "Not routed"}>
                      <span className="font-mono">{formatPhoneNumber(d.number)}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Callout tone="info">
              {old} becomes inbound only: it keeps ringing this campaign, so the leads it called can
              still call it back, and no call presents it again.
            </Callout>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="default" loading={replace.isPending} disabled={didId === null}>
              Replace
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}
